import { createComponent } from '@angular/core';
import type { ApplicationRef, ComponentRef, EnvironmentInjector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { ZodMiniType } from 'zod/mini';
import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import Component from '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js';
import type { Options as EditableOption } from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import type Cell from '@highcharts/dashboards/es-modules/Dashboards/Layout/Cell.js';
import { DASHBOARD_QUERY_CATALOG } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { isRecord } from '@h20/contracts/utils/object';
import { DASHBOARD_WIDGET_TYPE } from './dashboard-widget.contract';
import type { DashboardWidgetOptions } from './dashboard-widget.contract';
import { findReplacingWidget, findWidget } from './dashboard-widget-registry';
import type { DashboardLayoutCell, DashboardWidgetRegistration } from './dashboard-widget-registry';
import {
    boundQueryKey,
    dataBindingOptions,
    resolveLabels,
    settingsSignature,
} from './dashboard-editable-options';
import {
    fieldsFromSchema,
    isSettingsFieldValue,
    repairedConfigCount,
} from './dashboard-widget-schema';
import type { SettingsFields, SettingsFieldValue } from './dashboard-widget-schema';
import { translated } from '@h20-shared/utils/translate-utils';
import { ToastService } from '@h20-shared/services/toast.service';
import { canEditSettings, redrawSettingsPanel } from '../dashboard-settings-panel';
import { dataStateOf } from '../state/dashboard-data-state';
import { ProviderConnector } from '../data/provider-connector';
import { DashboardFiltersService } from '../filters/dashboard-filters.service';

/** The Angular context the widgets of one board are created with. */
interface WidgetHostContext {
    injector: EnvironmentInjector;
    appRef: ApplicationRef;
}

const hostContexts = new WeakMap<HTMLElement, WidgetHostContext>();

/** Binds the Angular context for widgets of the board mounted on `boardContainer`. */
export function bindWidgetHostContext(
    boardContainer: HTMLElement,
    injector: EnvironmentInjector,
    appRef: ApplicationRef
): void {
    hostContexts.set(boardContainer, { injector, appRef });
}

/** Finds the context bound for the board `container` belongs to */
function hostContextFor(container: HTMLElement): WidgetHostContext | undefined {
    for (let element: HTMLElement | null = container; element; element = element.parentElement) {
        const context = hostContexts.get(element);
        if (context) {
            return context;
        }
    }
    return undefined;
}

// .d.ts gap, both are real at runtime.
interface ComponentInternals {
    readonly contentElement: HTMLElement;
    emit(event: { type: string }): void;
}

/** The cell options the platform persists. Everything else on `DashboardWidgetOptions` is the base class's editing metadata. */
const SERIALIZED_OPTION_KEYS = [
    'renderTo',
    'id',
    'className',
    'title',
    'caption',
    'connector',
    'widgetKey',
    'widgetConfig',
    'queryKey',
    'queryParams',
] as const satisfies readonly (keyof DashboardWidgetOptions)[];

/**
 * Hosts a real Angular component inside a Dashboards cell.
 */
class DashboardWidgetHost extends Component {
    declare readonly options: DashboardWidgetOptions;
    /** The board this cell sits on. Real at runtime, missing from the `.d.ts`. */
    declare readonly board?: Board;

    static override defaultOptions: Partial<DashboardWidgetOptions> = {
        ...Component.defaultOptions,
        type: DASHBOARD_WIDGET_TYPE,
    };

    private readonly appRef: ApplicationRef;
    private readonly injector: EnvironmentInjector;
    private readonly translate: TranslateService;
    private readonly filterService: DashboardFiltersService;
    private readonly toast: ToastService;
    /** The host's own settings fields, before the mounted widget appends its config ones. */
    private readonly baseEditableOptions: readonly EditableOption[];
    private componentRef?: ComponentRef<unknown>;
    /** The key `componentRef` was built for */
    private mountedKey?: string;
    private configCache?: {
        readonly stored: unknown;
        readonly signature: string;
        readonly config: unknown;
    };
    /** What each settings dropdown offers */
    private settingsChoices: ReadonlyMap<string, readonly string[]> = new Map();
    private settingsDefaults: ReadonlyMap<string, SettingsFieldValue> = new Map();

    constructor(
        cell: Cell,
        options: Partial<DashboardWidgetOptions> | DashboardLayoutCell,
        board?: Board
    ) {
        // A cell saved as an old type, such as `HTML`.
        let replacementOptions: Partial<DashboardWidgetOptions> = {};
        const replacingWidgetKey = options.type && findReplacingWidget(options.type);
        if (replacingWidgetKey) {
            // The schema keeps the fields it knows, such as the old `html`, and the next save stores them as the config.
            const config = findWidget(replacingWidgetKey)?.configSchema?.parse(options);
            replacementOptions = {
                widgetKey: replacingWidgetKey,
                widgetConfig: isRecord(config) ? config : undefined,
            };
        }
        super(
            cell,
            { ...DashboardWidgetHost.defaultOptions, ...options, ...replacementOptions },
            board
        );
        this.type = DASHBOARD_WIDGET_TYPE;

        const context = board ? hostContextFor(board.container) : undefined;
        if (!context) {
            throw new Error('pulse-dashboard: no Angular context is bound for this board.');
        }

        this.appRef = context.appRef;
        this.injector = context.injector;
        this.translate = context.injector.get(TranslateService);
        this.filterService = context.injector.get(DashboardFiltersService);
        this.toast = context.injector.get(ToastService);
        this.baseEditableOptions = this.options.editableOptions ?? [];

        this.refreshSettingsFields();
        this.mountWidget();
    }

    /** Fills the cell with the component the current `widgetKey` names */
    private mountWidget(): void {
        const widgetKey = this.options.widgetKey;
        const widget = widgetKey === undefined ? undefined : findWidget(widgetKey);
        this.mountedKey = widgetKey;

        if (!widget) {
            // Unconfigured or unknown key: name the gap in the cell rather than throwing and
            // taking the rest of the board down with it.
            this.renderPlaceholder(widgetKey);
            return;
        }

        this.reportRepairedConfig(widget);
        this.componentRef = createComponent(widget.component, {
            environmentInjector: this.injector,
        });
        this.pushConfig();
        this.pushQueryKey();
        this.appRef.attachView(this.componentRef.hostView);
        (this as unknown as ComponentInternals).contentElement.appendChild(
            this.componentRef.location.nativeElement
        );

        this.pushFilters();
        this.pushRows();
        this.pushState();
        this.pushMeta();
    }

    /** Tells the reader when the cell's stored settings held something the widget could not read. */
    private reportRepairedConfig(widget: DashboardWidgetRegistration): void {
        const stored = this.options.widgetConfig;
        // A viewer cannot open a cell's settings, so telling them a setting is broken only worries
        // them about something they have no way to fix.
        if (
            widget.configSchema === undefined ||
            stored === undefined ||
            !canEditSettings(this.board)
        ) {
            return;
        }

        const repaired = repairedConfigCount(stored, this.parsedConfig());
        if (repaired === 0) {
            return;
        }

        this.toast.show({
            type: 'warning',
            heading: translated(this.translate, 'Dashboard.ConfigRepairedHeading'),
            body: translated(this.translate, 'Dashboard.ConfigRepaired', { count: repaired }),
            dedupeKey: `dashboard-config-repaired-${this.options.widgetKey ?? ''}`,
        });
    }

    /** Rebuilds the cell's settings fields from its current options */
    private refreshSettingsFields(): boolean {
        if (!canEditSettings(this.board)) {
            return false;
        }

        const widgetKey = this.options.widgetKey;
        const { fields, choices, defaults } = this.settingsFields(
            widgetKey === undefined ? undefined : findWidget(widgetKey)
        );
        this.settingsChoices = choices;
        this.settingsDefaults = defaults;
        const resolved = resolveLabels(fields, this.translate);
        const changed =
            settingsSignature(resolved) !== settingsSignature(this.options.editableOptions ?? []);
        this.options.editableOptions = resolved;
        return changed;
    }

    private configSchema(): ZodMiniType | undefined {
        const widgetKey = this.options.widgetKey;
        if (widgetKey === undefined) {
            return undefined;
        }
        return findWidget(widgetKey)?.configSchema;
    }

    private parsedConfig(): unknown {
        const stored = this.options.widgetConfig;
        const schema = this.configSchema();
        if (schema === undefined) {
            return stored;
        }

        const cache = this.configCache;
        if (cache !== undefined && cache.stored === stored) {
            return cache.config;
        }

        // Board updates copy the options, so an unchanged config arrives as a new object.
        const signature = JSON.stringify(stored) ?? '';
        let config: unknown;
        if (cache !== undefined && cache.signature === signature) {
            config = cache.config;
        } else {
            config = schema.parse(stored);
        }

        this.configCache = { stored, signature, config };
        return config;
    }

    /** What the settings panel shows as a field's current value. */
    override getEditableOptionValue(
        propertyPath?: (string | number)[]
    ): number | boolean | undefined | string {
        if (propertyPath?.[0] !== 'widgetConfig' || this.configSchema() === undefined) {
            return super.getEditableOptionValue(propertyPath);
        }

        let level: unknown = this.parsedConfig();
        for (const key of propertyPath.slice(1)) {
            level = isRecord(level) ? level[key] : undefined;
        }

        const path = propertyPath.join('.');
        if (level === undefined || level === '') {
            level = this.settingsDefaults.get(path) ?? level;
        }
        if (isSettingsFieldValue(level)) {
            // The saved value may not be in the list, for example a column picked before the cell changed query.
            const choices = this.settingsChoices.get(path);
            if (choices === undefined || choices.includes(String(level))) {
                return level;
            }
            return choices[0];
        }

        // Nothing saved for this field
        return undefined;
    }

    /** Which fields the cell's settings panel shows for the mounted widget */
    private settingsFields(widget: DashboardWidgetRegistration | undefined): SettingsFields {
        if (!widget) {
            return {
                fields: [...this.baseEditableOptions],
                choices: new Map(),
                defaults: new Map(),
            };
        }

        const queryKey = boundQueryKey(this.options);
        const columns = queryKey === undefined ? [] : DASHBOARD_QUERY_CATALOG[queryKey].columns;
        const context = { config: this.parsedConfig(), queryKey, columns };
        const fromSchema =
            widget.configSchema === undefined
                ? undefined
                : fieldsFromSchema(widget.configSchema, context);

        const { title, caption } = this.options;
        let titleFields = this.baseEditableOptions;
        if (widget.hasTitle === false && !title && !caption) {
            titleFields = [];
        }

        return {
            fields: [
                ...titleFields,
                ...(widget.dataBound === false ? [] : dataBindingOptions(queryKey)),
                ...(fromSchema?.fields ?? []),
            ],
            choices: fromSchema?.choices ?? new Map(),
            defaults: fromSchema?.defaults ?? new Map(),
        };
    }

    /** Empties the cell, so a remount starts from bare content. */
    private unmountWidget(): void {
        this.configCache = undefined;
        if (this.componentRef) {
            this.appRef.detachView(this.componentRef.hostView);
            this.componentRef.destroy();
            this.componentRef = undefined;
        }
        const content = (this as unknown as ComponentInternals).contentElement;
        while (content.firstChild) {
            content.firstChild.remove();
        }
    }

    override async update(
        newOptions: Partial<DashboardWidgetOptions>,
        shouldRerender?: boolean
    ): Promise<void> {
        await super.update(newOptions, shouldRerender);

        if (this.options.widgetKey !== this.mountedKey) {
            this.unmountWidget();
            this.mountWidget();
        } else {
            this.pushConfig();
            this.pushQueryKey();
        }

        if (this.refreshSettingsFields()) {
            redrawSettingsPanel(this);
        }

        this.markRendered();
    }

    override getOptionsOnDrop(): Partial<DashboardWidgetOptions> {
        return { type: DASHBOARD_WIDGET_TYPE };
    }

    getOptions(): Partial<DashboardWidgetOptions> {
        const serialized: Partial<DashboardWidgetOptions> = { type: DASHBOARD_WIDGET_TYPE };
        const keep = <K extends (typeof SERIALIZED_OPTION_KEYS)[number]>(key: K): void => {
            const value = this.options[key];
            if (value !== undefined && value !== '' && value !== false) {
                serialized[key] = value;
            }
        };
        for (const key of SERIALIZED_OPTION_KEYS) {
            keep(key);
        }
        return serialized;
    }

    override async initConnectors(): Promise<this> {
        await super.initConnectors();
        this.pushQueryKey();
        this.pushFilters();
        this.pushRows();
        this.pushState();
        this.pushMeta();
        this.markRendered();
        return this;
    }

    onTableChanged(): void {
        this.pushRows();
        this.pushState();
        this.pushMeta();
    }

    /** Emits `afterRender` to clear the cell loading overlay. */
    private markRendered(): void {
        (this as unknown as ComponentInternals).emit({ type: 'afterRender' });
    }

    override destroy(): void {
        this.unmountWidget();
        super.destroy();
    }

    /** Says why the cell is blank, so an unconfigured widget reads as unconfigured. */
    private renderPlaceholder(widgetKey: string | undefined): void {
        const i18nKey =
            widgetKey === undefined ? 'Dashboard.WidgetNoneSelected' : 'Dashboard.WidgetUnknown';
        const placeholder = document.createElement('div');
        placeholder.className = 'pulse-dashboard__widget-placeholder';
        placeholder.textContent = translated(this.translate, i18nKey, { widgetKey });
        (this as unknown as ComponentInternals).contentElement.appendChild(placeholder);
    }

    /** Hands the widget the cell's authored config. */
    private pushConfig(): void {
        this.componentRef?.setInput('config', this.parsedConfig());
    }

    /** Tells the widget which query feeds it */
    private pushQueryKey(): void {
        this.componentRef?.setInput('queryKey', boundQueryKey(this.options));
    }

    private pushRows(): void {
        // No widget mounted, or no cell connector: the widget fetches its own data.
        if (!this.componentRef || this.connectorHandlers.length === 0) {
            return;
        }
        // `getRowObjects()` returns a fresh array, so setInput's `Object.is` de-dupe never swallows an update.
        this.componentRef.setInput(
            'rows',
            this.getDataTable()?.getModified().getRowObjects() ?? []
        );
    }

    private pushFilters(): void {
        if (!this.componentRef || this.connectorHandlers.length === 0) {
            return;
        }
        const table = this.getDataTable();
        this.componentRef.setInput(
            'filters',
            table === undefined ? undefined : this.filterService.forTable(table)
        );
    }

    /** Hands the widget how its dataset is doing. */
    private pushState(): void {
        // No widget mounted, or no cell connector: nothing to report.
        if (!this.componentRef || this.connectorHandlers.length === 0) {
            return;
        }
        this.componentRef.setInput('state', dataStateOf(this));
    }

    /** Hands the widget what the query said about its rows, such as when they were calculated. */
    private pushMeta(): void {
        if (!this.componentRef || this.connectorHandlers.length === 0) {
            return;
        }
        const connector = this.connectorHandlers[0].connector;
        const meta = connector instanceof ProviderConnector ? connector.meta : undefined;
        this.componentRef.setInput('meta', meta);
    }
}

/** Registers `DashboardWidget` with Dashboards' own component-type registry. */
declare module '@highcharts/dashboards/es-modules/Dashboards/Components/ComponentType' {
    interface ComponentTypeRegistry {
        DashboardWidget: typeof DashboardWidgetHost;
    }
}

export { DashboardWidgetHost };
